"use client";

import { useEffect, useState } from "react";
import { CampaignTradingMode, StreakSummaryResponse, StreakSummaryResponseV2 } from "@cede/types";
import { useAutomaticStreakConfig } from "./useAutomaticStreakConfig";
import { useUserStreaks } from "./useUserStreaks";
import { resolveTradingMode } from "@/utils/campaign";
import { useParams } from "next/navigation";

export type TradeWidgetTab = "manual" | "automatic";

type UnifiedStreakSummary = StreakSummaryResponse | StreakSummaryResponseV2;

type UseCampaignAutopilotTabsParams = {
  /** Campaign the tabs resolve against. Pass the copy the caller already loaded to avoid a fetch race. */
  campaign: Parameters<typeof resolveTradingMode>[0];
  /** `?tab=` query param when the caller reads the URL; `autopilot` deep-links to the autopilot tab. */
  tabParam?: string | null;
};

/**
 * Manual / Autopilot tab availability for a campaign trade surface.
 *
 * Every layout that can host the autopilot widget (CEX spot via TradeWidget,
 * Hyperliquid) resolves it the same way, so the rules live here:
 * - `manual_only`: manual only, autopilot API blocked
 * - `both`: both tabs
 * - `streak_only`: autopilot only, manual trades not eligible for rewards
 *
 * Exception: a user with a running streak keeps the autopilot tab even on a
 * `manual_only` campaign, otherwise they have no way to stop it.
 */
export const useCampaignAutopilotTabs = ({ campaign, tabParam = null }: UseCampaignAutopilotTabsParams) => {
  const [activeTab, setActiveTab] = useState<TradeWidgetTab>(tabParam === "autopilot" ? "automatic" : "manual");

  // Only set on campaign pages (/[id]), not on the generic /trade page.
  const { id: campaignName } = useParams<{ id: string }>();
  const { isEnabled: isAutoStreakEnabled } = useAutomaticStreakConfig(campaignName);
  // Without a campaign the list endpoint returns every streak the user has, so a
  // `recentStreak` from an unrelated campaign would surface on the generic /trade page.
  const { streaks, activeStreaks } = useUserStreaks({ campaignName: campaignName ?? undefined });

  const hasCampaignContext = !!campaignName;
  const tradingMode = resolveTradingMode(campaign);
  const isStreakOnlyMode = tradingMode === CampaignTradingMode.STREAK_ONLY;
  const hasActiveStreaks = activeStreaks.length > 0;

  const showManualTab = tradingMode === CampaignTradingMode.BOTH || tradingMode === CampaignTradingMode.MANUAL_ONLY;
  const showAutopilotTab =
    hasCampaignContext &&
    ((isAutoStreakEnabled && (tradingMode === CampaignTradingMode.BOTH || isStreakOnlyMode)) || hasActiveStreaks);
  const showTabs = showManualTab && showAutopilotTab;

  // Force the autopilot widget ONLY in streak_only mode (manual is hidden entirely).
  const forceAutopilotWidget = hasCampaignContext && isStreakOnlyMode;

  useEffect(() => {
    if (tabParam === "autopilot" && showAutopilotTab) {
      setActiveTab("automatic");
    } else if (forceAutopilotWidget) {
      setActiveTab("automatic");
    } else if (!showAutopilotTab) {
      setActiveTab("manual");
    }
  }, [tabParam, showAutopilotTab, forceAutopilotWidget]);

  return {
    activeTab,
    setActiveTab,
    tradingMode,
    showManualTab,
    showAutopilotTab,
    showTabs,
    forceAutopilotWidget,
    hasActiveStreaks,
    /** Most recent streak for this campaign, sorted by creation date desc. */
    recentStreak: hasCampaignContext && streaks.length > 0 ? (streaks[0] as UnifiedStreakSummary) : undefined,
  };
};
